import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList, Clock, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const ContractLaborDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["cl_profile", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_profiles")
        .select("first_name, last_name, display_name, role:roles(name)")
        .eq("user_id", user!.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  const { data: assignedWOs = [] } = useQuery({
    queryKey: ["cl_assigned_wos", user?.id],
    queryFn: async () => {
      // Work orders assigned via vendor link or direct user assignment
      const { data } = await supabase
        .from("work_orders")
        .select("id, number, status, description, projects(name)")
        .order("created_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
    enabled: !!user,
  });

  const firstName = profile?.display_name?.split(" ")[0] ?? profile?.first_name ?? "Contractor";
  const initials = `${(profile?.first_name ?? "C")[0]}${(profile?.last_name ?? "L")[0]}`.toUpperCase();

  if (isLoading) return (
    <div className="space-y-6">
      <Skeleton className="h-16 w-64" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14">
          <AvatarFallback className="text-lg bg-violet-600 text-white">{initials}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-2xl font-bold">Welcome, {firstName}</h1>
          <p className="text-muted-foreground text-sm">Contract Labor Portal</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Assigned Work Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{assignedWOs.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Log Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button size="sm" variant="outline" onClick={() => navigate("/portal/contract-labor/log-hours")}>
              Log Today's Hours
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button size="sm" variant="outline" onClick={() => navigate("/portal/contract-labor/documents")}>
              View Documents
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Work Orders */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Work Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {assignedWOs.length === 0 ? (
            <p className="text-muted-foreground text-sm">No work orders assigned.</p>
          ) : (
            <div className="space-y-2">
              {assignedWOs.map((wo: any) => (
                <div key={wo.id} className="flex items-center justify-between border rounded p-3">
                  <div>
                    <p className="font-mono text-sm font-medium">{wo.number}</p>
                    <p className="text-xs text-muted-foreground">{(wo.projects as any)?.name ?? "—"}</p>
                    {wo.description && <p className="text-xs text-muted-foreground truncate max-w-[300px]">{wo.description}</p>}
                  </div>
                  <Badge variant="outline" className="capitalize text-[10px]">{wo.status}</Badge>
                </div>
              ))}
            </div>
          )}
          <Button variant="link" className="mt-2 px-0 text-sm" onClick={() => navigate("/portal/contract-labor/work-orders")}>
            View all work orders →
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractLaborDashboard;
